/**
 * The /auth/callback landing page.
 *
 * Runs inside the popup: read the result off the URL, hand it to the opener,
 * close. Deliberately does no API work of its own — the opener holds the session
 * and the React Query cache, so it should be the one to redeem the code.
 *
 * Two hard requirements:
 *  - this route must sit OUTSIDE the auth guard, or the redirect back from the
 *    provider bounces to a login screen and the flow dies;
 *  - `public/_redirects` must exist, or a hard load of this path 404s in
 *    production.
 */

import { useEffect, useState } from 'react'
import type { CallbackMessage } from './popup.handler'

export function CallbackPage() {
  const [message, setMessage] = useState('Finishing sign-in…')
  const [stranded, setStranded] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const payload: CallbackMessage = {
      source: 'context-graph-oauth',
      code: params.get('code') ?? undefined,
      state: params.get('state') ?? undefined,
      error: params.get('error') ?? undefined,
      error_description: params.get('error_description') ?? undefined,
    }

    if (payload.error) {
      setMessage(payload.error_description || `Authorization failed: ${payload.error}`)
    } else if (!payload.code) {
      setMessage('This page was opened without an authorization code.')
    }

    // No opener means the provider redirected the top-level window (or the popup
    // reference was lost). Say so rather than spinning forever.
    if (!window.opener || window.opener === window) {
      setStranded(true)
      return
    }

    window.opener.postMessage(payload, window.location.origin)
    // Give the opener a beat to receive it before the window disappears.
    const t = setTimeout(() => {
      try {
        window.close()
      } catch {
        setStranded(true)
      }
    }, 400)
    return () => clearTimeout(t)
  }, [])

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div className="card" style={{ maxWidth: 440 }}>
        <h2>{stranded ? 'Almost there' : 'Connecting'}</h2>
        <p className="muted" style={{ marginTop: 8 }}>
          {message}
        </p>
        {stranded && (
          <p className="small muted">
            You can close this window and return to the app — the connection will
            appear on the Connections page.
          </p>
        )}
        {stranded && (
          <a className="btn btn-primary" href="/connections" style={{ marginTop: 10 }}>
            Go to Connections
          </a>
        )}
      </div>
    </div>
  )
}
